/**
 * Pacing History page — end-of-month pacing table for a single portfolio company.
 * Shows historical pacing (Total Sales / Monthly Budget) alongside current MTD pacing.
 * In live mode: requires authentication via MsalAuthenticationTemplate.
 * In demo mode: loads data without authentication.
 */

import { useParams, Link } from "react-router-dom";
import { MsalAuthenticationTemplate, useMsal } from "@azure/msal-react";
import { InteractionStatus, InteractionType } from "@azure/msal-browser";
import { useCallback } from "react";
import { powerbiRequest } from "../authConfig";
import { Loading } from "../ui-components/Loading";
import { LoadingNotice } from "../ui-components/LoadingNotice";
import { ErrorComponent } from "../ui-components/ErrorComponent";
import { MetricCard } from "../ui-components/MetricCard";
import { PortcoSubNav } from "../ui-components/PortcoSubNav";
import {
  isDemoMode,
  MonthlyTrendPoint,
  PortcoMetrics,
} from "../utils/PowerBIApiCall";
import { usePacingData } from "../hooks/usePacingData";
import { formatPct } from "../utils/format";

// =============================================================================
// Helpers
// =============================================================================

// Kept local (not the shared compact formatter): this page formats budget
// VARIANCE, which is routinely negative, and negatives here fall through to
// full "$-52,340" rendering — the shared compact would change that text.
function formatCurrency(value: number): string {
  if (value >= 1_000_000) {
    return `$${(value / 1_000_000).toFixed(1)}M`;
  }
  if (value >= 1_000) {
    return `$${(value / 1_000).toFixed(0)}K`;
  }
  return `$${value.toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`;
}

/** Performance color class based on pacing threshold */
function pacingColor(pct: number): string {
  if (pct >= 100) return "text-emerald-600";
  if (pct >= 95) return "text-amber-600";
  return "text-red-600";
}

/** Performance background color for the indicator dot */
function pacingDotBg(pct: number): string {
  if (pct >= 100) return "bg-emerald-500";
  if (pct >= 95) return "bg-amber-500";
  return "bg-red-500";
}

/** Status label */
function pacingLabel(pct: number): string {
  if (pct >= 100) return "On Track";
  if (pct >= 95) return "At Risk";
  return "Behind";
}

/** Check if a month is the current month */
function isCurrentMonth(monthStart: string): boolean {
  const d = new Date(monthStart);
  const now = new Date();
  return (
    d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth()
  );
}

// =============================================================================
// Shared UI — pure presentational component
// =============================================================================

interface PacingHistoryUIProps {
  monthlyTrend: MonthlyTrendPoint[] | null;
  metrics: PortcoMetrics | null;
  portcoId?: string;
  loading: boolean;
  error: string | null;
}

function PacingHistoryUI({
  monthlyTrend,
  metrics,
  portcoId,
  loading,
  error,
}: PacingHistoryUIProps) {
  if (loading) {
    return (
      <div className="w-full max-w-6xl space-y-6">
        <LoadingNotice message="Loading pacing history — this can take a few seconds…" />
        <div className="flex items-center justify-between mb-2">
          <div className="h-4 w-32 bg-gray-200 rounded animate-pulse" />
        </div>
        <div>
          <div className="h-8 w-72 bg-gray-200 rounded animate-pulse mb-2" />
          <div className="h-4 w-48 bg-gray-100 rounded animate-pulse" />
        </div>
        <div className="grid grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => (
            <div
              key={i}
              className="bg-white border border-gray-100 rounded-xl p-5"
            >
              <div className="h-3 w-20 bg-gray-100 rounded animate-pulse mx-auto mb-3" />
              <div className="h-7 w-28 bg-gray-200 rounded animate-pulse mx-auto" />
            </div>
          ))}
        </div>
        <div className="bg-white border border-gray-100 rounded-xl overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100">
            <div className="h-5 w-40 bg-gray-200 rounded animate-pulse" />
          </div>
          {[...Array(6)].map((_, i) => (
            <div
              key={i}
              className="px-6 py-4 border-b border-gray-50 flex items-center gap-6"
            >
              <div className="flex-1 grid grid-cols-5 gap-4">
                {[...Array(5)].map((_, j) => (
                  <div
                    key={j}
                    className="h-4 bg-gray-100 rounded animate-pulse"
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full max-w-lg">
        <div className="bg-white border border-red-100 rounded-xl shadow-sm overflow-hidden">
          <div className="p-6">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-lg bg-red-50 flex items-center justify-center flex-shrink-0 mt-0.5">
                <svg
                  className="w-4 h-4 text-red-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
                  />
                </svg>
              </div>
              <div>
                <h3 className="text-gray-900 font-semibold mb-1">
                  Something went wrong
                </h3>
                <p className="text-gray-500 text-sm">{error}</p>
              </div>
            </div>
          </div>
        </div>
        {portcoId && (
          <Link
            to={`/portco/${portcoId}`}
            className="inline-flex items-center gap-1.5 mt-4 text-sm text-gray-500 hover:text-wmp-500 transition-colors no-underline"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            Back to Detail
          </Link>
        )}
      </div>
    );
  }

  const portcoName = metrics?.portcoName || "Portfolio Company";

  // Filter to months that have budget data for pacing calculation, current year only
  const currentYear = new Date().getFullYear();
  const pacingMonths = (monthlyTrend || []).filter(
    (m) =>
      m.pacingPct !== null &&
      m.monthlyBudget > 0 &&
      new Date(m.monthStart).getFullYear() === currentYear,
  );

  // Most recent first for the table
  const sortedMonths = [...pacingMonths].reverse();

  // Compute summary stats
  const completedMonths = pacingMonths.filter(
    (m) => !isCurrentMonth(m.monthStart),
  );
  const avgPacing =
    completedMonths.length > 0
      ? completedMonths.reduce((sum, m) => sum + (m.pacingPct || 0), 0) /
        completedMonths.length
      : null;
  const last3 = completedMonths.slice(-3);
  const avg3mo =
    last3.length > 0
      ? last3.reduce((sum, m) => sum + (m.pacingPct || 0), 0) / last3.length
      : null;

  // Current MTD pacing from live metrics
  const currentMtdPacing =
    metrics?.mtd && metrics.mtd.budgetProrated > 0
      ? (metrics.mtd.actual / metrics.mtd.budgetProrated) * 100
      : null;

  return (
    <div className="w-full max-w-6xl">
      {/* Breadcrumb + sub-nav */}
      <div className="mb-5 space-y-3">
        <Link
          to={portcoId ? `/portco/${portcoId}` : "/dashboard"}
          className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-wmp-500 transition-colors no-underline group"
        >
          <svg
            className="w-4 h-4 text-gray-400 group-hover:text-wmp-500 transition-colors"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          {portcoName || "Overview"}
        </Link>
        {portcoId && <PortcoSubNav portcoId={portcoId} />}
      </div>

      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-900 tracking-tight">
          {portcoName} — Pacing History
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          End-of-month pacing (Total Sales / Monthly Budget) — {currentYear}
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <MetricCard
          label="Current MTD Pace"
          value={currentMtdPacing !== null ? formatPct(currentMtdPacing) : "—"}
          highlighted
          color={
            currentMtdPacing !== null
              ? currentMtdPacing >= 100
                ? "#2DA770"
                : currentMtdPacing >= 95
                  ? "#d97706"
                  : "#EF4444"
              : undefined
          }
          formula="Actual / Budget (Prorated)"
          formulaValues={
            metrics?.mtd
              ? `= ${formatCurrency(metrics.mtd.actual)} / ${formatCurrency(metrics.mtd.budgetProrated)}`
              : undefined
          }
        />
        <MetricCard
          label="3-Month Avg"
          value={avg3mo !== null ? formatPct(avg3mo) : "—"}
          color={
            avg3mo !== null
              ? avg3mo >= 100
                ? "#2DA770"
                : avg3mo >= 95
                  ? "#d97706"
                  : "#EF4444"
              : undefined
          }
        />
        <MetricCard
          label="Historical Avg"
          value={avgPacing !== null ? formatPct(avgPacing) : "—"}
          variance={
            completedMonths.length > 0
              ? `${completedMonths.length} months`
              : undefined
          }
          color={
            avgPacing !== null
              ? avgPacing >= 100
                ? "#2DA770"
                : avgPacing >= 95
                  ? "#d97706"
                  : "#EF4444"
              : undefined
          }
        />
      </div>

      {/* Pacing Table */}
      {sortedMonths.length === 0 ? (
        <div className="bg-white border border-gray-100 rounded-xl p-8 text-center">
          <p className="text-gray-500 text-sm">
            No pacing data available. Monthly budget data is required to
            calculate pacing.
          </p>
        </div>
      ) : (
        <div className="bg-white border border-gray-100 rounded-xl shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100">
            <h2 className="text-lg font-semibold text-gray-900">
              Monthly Pacing
            </h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="px-6 py-3 text-left text-[11px] font-semibold tracking-wider text-gray-400 uppercase">
                    Month
                  </th>
                  <th className="px-6 py-3 text-right text-[11px] font-semibold tracking-wider text-gray-400 uppercase">
                    Total Sales
                  </th>
                  <th className="px-6 py-3 text-right text-[11px] font-semibold tracking-wider text-gray-400 uppercase">
                    Monthly Budget
                  </th>
                  <th className="px-6 py-3 text-right text-[11px] font-semibold tracking-wider text-gray-400 uppercase">
                    Variance
                  </th>
                  <th className="px-6 py-3 text-right text-[11px] font-semibold tracking-wider text-gray-400 uppercase">
                    Pacing
                  </th>
                  <th className="px-6 py-3 text-center text-[11px] font-semibold tracking-wider text-gray-400 uppercase">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedMonths.map((month, idx) => {
                  const isCurrent = isCurrentMonth(month.monthStart);
                  const pct = month.pacingPct!;
                  const variance = month.totalSales - month.monthlyBudget;
                  return (
                    <tr
                      key={month.monthStart}
                      className={`border-b border-gray-50 ${
                        isCurrent
                          ? "bg-wmp-100/40"
                          : idx % 2 === 1
                            ? "bg-gray-50/50"
                            : ""
                      } hover:bg-gray-50 transition-colors`}
                    >
                      <td className="px-6 py-3.5">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-gray-900">
                            {month.periodNameFormatted}
                          </span>
                          {isCurrent && (
                            <span className="text-[10px] font-semibold tracking-wider text-wmp-500 bg-wmp-500/10 px-1.5 py-0.5 rounded uppercase">
                              MTD
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-3.5 text-right">
                        <span className="text-sm font-medium text-gray-900 tabular-nums">
                          {formatCurrency(month.totalSales)}
                        </span>
                      </td>
                      <td className="px-6 py-3.5 text-right">
                        <span className="text-sm text-gray-600 tabular-nums">
                          {formatCurrency(month.monthlyBudget)}
                        </span>
                      </td>
                      <td className="px-6 py-3.5 text-right">
                        <span
                          className={`text-sm font-medium tabular-nums ${variance >= 0 ? "text-emerald-600" : "text-red-600"}`}
                        >
                          {variance >= 0 ? "+" : ""}
                          {formatCurrency(variance)}
                        </span>
                      </td>
                      <td className="px-6 py-3.5 text-right">
                        <span
                          className={`text-sm font-semibold tabular-nums ${pacingColor(pct)}`}
                        >
                          {formatPct(pct)}
                        </span>
                      </td>
                      <td className="px-6 py-3.5 text-center">
                        <div className="flex items-center justify-center gap-1.5">
                          <span
                            className={`w-2 h-2 rounded-full ${pacingDotBg(pct)}`}
                          />
                          <span
                            className={`text-xs font-medium ${pacingColor(pct)}`}
                          >
                            {pacingLabel(pct)}
                          </span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Demo mode data loader — no MSAL hooks
// =============================================================================

function DemoPacingContent() {
  const { portcoId } = useParams<{ portcoId: string }>();
  const data = usePacingData(portcoId);

  return (
    <PacingHistoryUI
      monthlyTrend={data.monthlyTrend}
      metrics={data.metrics}
      portcoId={portcoId}
      loading={data.loading}
      error={data.error}
    />
  );
}

// =============================================================================
// Live mode data loader — uses MSAL hooks (must be inside MsalProvider)
// =============================================================================

function LivePacingContent() {
  const { portcoId } = useParams<{ portcoId: string }>();
  const { instance, inProgress } = useMsal();

  const handleAuthError = useCallback(() => {
    instance.acquireTokenRedirect({
      ...powerbiRequest,
      account: instance.getActiveAccount()!,
    });
  }, [instance]);

  const data = usePacingData(
    portcoId,
    inProgress === InteractionStatus.None,
    handleAuthError,
  );

  return (
    <PacingHistoryUI
      monthlyTrend={data.monthlyTrend}
      metrics={data.metrics}
      portcoId={portcoId}
      loading={data.loading}
      error={data.error}
    />
  );
}

// =============================================================================
// Exported PacingHistory component — routes to demo or live mode
// =============================================================================

export function PacingHistory() {
  if (isDemoMode()) {
    return <DemoPacingContent />;
  }

  return (
    <MsalAuthenticationTemplate
      interactionType={InteractionType.Redirect}
      authenticationRequest={powerbiRequest}
      errorComponent={ErrorComponent}
      loadingComponent={Loading}
    >
      <LivePacingContent />
    </MsalAuthenticationTemplate>
  );
}
